import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, PartyPopper, PawPrint, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          // ---- Google Ads conversion (once per session) ----
          if (typeof window !== "undefined" && window.gtag && !window.__pawConvFired) {
            window.__pawConvFired = true;
            // Enhanced Conversions: hashed Google-side
            if (data.email) {
              window.gtag("set", "user_data", { email: data.email });
            }
            window.gtag("event", "conversion", {
              send_to: "AW-18135331023/7mmpCIzp47EcEM_hzMdD",
              value: typeof data.amount === "number" ? data.amount : undefined,
              currency: (data.currency || "USD").toUpperCase(),
              transaction_id: sessionId,
            });
          }
          // ---- Meta Pixel Purchase (once per session) ----
          if (typeof window !== "undefined" && window.fbq && !window.__pawFbConvFired) {
            window.__pawFbConvFired = true;
            window.fbq(
              "track",
              "Purchase",
              {
                value: typeof data.amount === "number" ? data.amount : 0,
                currency: (data.currency || "USD").toUpperCase(),
                content_ids: ["pawventure-rugby-ball"],
                content_type: "product",
                content_name: "Pawventure Squeaky Rugby Ball",
              },
              { eventID: sessionId }
            );
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="rounded-3xl border-border max-w-lg w-full p-10 text-center" data-testid="success-card">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-primary animate-spin" size={40} strokeWidth={2.5} />
            <h1 className="font-display text-2xl font-bold mt-5">Confirming your order...</h1>
            <p className="text-muted-foreground mt-2">Hang tight — checking your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <PartyPopper className="text-primary" size={30} strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl font-bold mt-5" data-testid="success-title">Payment received!</h1>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Thanks for your order. We'll email your confirmation and tracking details shortly.
            </p>
            {order && (
              <div className="mt-6 rounded-2xl bg-muted/40 p-4 text-sm flex justify-between">
                <span className="text-muted-foreground">Total paid</span>
                <span className="font-semibold">${order.amount?.toFixed?.(2) ?? order.amount}</span>
              </div>
            )}
            <Link to="/">
              <Button data-testid="back-home" className="mt-7 rounded-full h-12 px-8 font-semibold">
                <PawPrint size={16} className="mr-2" strokeWidth={2.5} /> Back to shop
              </Button>
            </Link>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="text-destructive" size={30} strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5">Hmm, something went wrong.</h1>
            <p className="text-muted-foreground mt-2">
              We couldn't confirm your payment. If money was deducted, contact us — otherwise try again.
            </p>
            <Link to="/">
              <Button data-testid="retry-home" className="mt-6 rounded-full h-12 px-8 font-semibold">
                Back to shop
              </Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
